import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import frLocale from '@fullcalendar/core/locales/fr';
import enLocale from '@fullcalendar/core/locales/en-gb';
import { CalendarEvent as DBCalendarEvent, FullCalendarEvent, Lead, Project } from '../../types';
import { useI18n } from '../../context/I18nContext';
import FollowUpModal from '../leads/FollowUpModal';
import Card from '../ui/Card';
import Button from '../ui/Button';
import Modal from '../ui/Modal';
import Input from '../ui/Input';
import TextArea from '../ui/TextArea';
import Select from '../ui/Select';
import DatePicker from '../ui/DatePicker';
import type { RequestCreateLeadArgs } from '../../hooks/useCreateLeadFromSearch';
import LeadSearch from '../messages/LeadSearch';
import { ChevronLeft, ChevronRight, CalendarDays, Link2 } from 'lucide-react';
import SegmentedToggle from '../ui/SegmentedToggle';
import { calendarEventColors, getCalendarEventTextColor } from '../../styles/colors';
import { useAppearance } from '../../context/AppearanceContext';
import type { CalendarEventTypeId } from '../../styles/calendarEventTypeColors';
import { useMediaQuery, MOBILE_BREAKPOINT, MOBILE_BREAKPOINT_PX } from '../../hooks/useMediaQuery';
import { googleCalendarService } from '../../services/googleCalendar';
import { navigateToProject } from '../../utils/navigationUtils';
import { Link, useNavigate } from 'react-router-dom';
import type { EventContentArg } from '@fullcalendar/core';
/** Default time slot when creating an event from month view (day click has no time). */
const DEFAULT_NEW_EVENT_START_HOUR = 9;
const DEFAULT_NEW_EVENT_END_HOUR = 10;
const ONE_HOUR_MS = 60 * 60 * 1000;

/** Nearest vertically scrollable ancestor (FullCalendar month has multiple .fc-scroller nodes). */
function getVerticalScrollParent(el: HTMLElement): HTMLElement | null {
  let node: HTMLElement | null = el.parentElement;
  while (node && node !== document.body) {
    const { overflowY } = getComputedStyle(node);
    if (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay') {
      return node;
    }
    node = node.parentElement;
  }
  return null;
}

/**
 * Vertical scroll for month view — same idea as scrollTime=08:00 for week/day:
 * land on "today" instead of the top of the month when the grid overflows.
 */
function scrollMonthViewToToday(root: HTMLElement | null | undefined): boolean {
  if (!root) return false;
  const todayCell = root.querySelector(
    '.fc-dayGridMonth-view td.fc-day-today, .fc-dayGridMonth-view .fc-day-today'
  ) as HTMLElement | null;
  if (!todayCell) return false;

  const scroller = getVerticalScrollParent(todayCell);
  if (!scroller) return false;
  if (scroller.scrollHeight <= scroller.clientHeight + 1) return false;

  const scrollerRect = scroller.getBoundingClientRect();
  const cellRect = todayCell.getBoundingClientRect();
  const delta =
    cellRect.top + cellRect.height / 2 - (scrollerRect.top + scrollerRect.height / 2);
  scroller.scrollTop += delta;
  return true;
}

type CalendarViewType = 'month' | 'week' | 'day';

interface CalendarProps {
  events: DBCalendarEvent[];
  leads: Lead[];
  rightsProjects?: Project[];
  onAddEvent: (event: Omit<DBCalendarEvent, 'id'>) => void;
  onUpdateEvent: (event: DBCalendarEvent, options?: { previousItem?: DBCalendarEvent }) => void;
  onDeleteEvent: (id: string) => void;
  isModalOpen?: boolean;
  onModalClose?: () => void;
  onViewDatesChange?: (start: Date, end: Date) => void;
  onSearchLeads?: (term: string) => Promise<Lead[]>;
  onCreateLead?: (args: RequestCreateLeadArgs) => void;
}

const Calendar: React.FC<CalendarProps> = ({
  events,
  leads,
  rightsProjects = [],
  onAddEvent,
  onUpdateEvent,
  onDeleteEvent,
  isModalOpen: externalIsModalOpen,
  onModalClose: externalOnModalClose,
  onViewDatesChange,
  onSearchLeads,
  onCreateLead,
}) => {
  const { t, language } = useI18n();
  const { eventTypeColors } = useAppearance();
  const navigate = useNavigate();
  const calendarRef = useRef<FullCalendar>(null);
  const calendarHostRef = useRef<HTMLDivElement>(null);
  // Prevent out-of-order follow-up drops from reverting newer moves
  const followUpDropSeqRef = useRef<Record<string, number>>({});
  const [internalIsModalOpen, setInternalIsModalOpen] = useState(false);
  // Use internal state if it's true (user clicked on event), otherwise use external state if provided
  const isModalOpen = internalIsModalOpen || (externalIsModalOpen !== undefined ? externalIsModalOpen : false);
  
  // Handle modal state: if external control, we need separate open/close handlers
  const openModal = () => {
    // Always use internal state - external control is only for initial opening from parent
    setInternalIsModalOpen(true);
  };
  
  const handleModalClose = () => {
    setInternalIsModalOpen(false);
    setEditingEvent(null);
    setSelectedDate(null);
    // Also notify external handler if provided
    if (externalOnModalClose) {
      externalOnModalClose();
    }
  };
  const [editingEvent, setEditingEvent] = useState<FullCalendarEvent | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [isFollowUpModalOpen, setIsFollowUpModalOpen] = useState(false);
  const [currentView, setCurrentView] = useState<CalendarViewType>('month');

  const isMobile = useMediaQuery(MOBILE_BREAKPOINT);

  const scrollToTodayInMonthIfNeeded = useCallback(() => {
    const api = calendarRef.current?.getApi();
    if (!api || api.view.type !== 'dayGridMonth') return;

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const { activeStart, activeEnd } = api.view;
    if (today < activeStart || today >= activeEnd) return;

    // Layout can settle after datesSet/updateSize — retry briefly like waiting for scrollTime.
    let tries = 0;
    const maxTries = 8;
    const tick = () => {
      const ok = scrollMonthViewToToday(calendarHostRef.current);
      tries += 1;
      if (!ok && tries < maxTries) {
        window.setTimeout(tick, 40);
      }
    };
    requestAnimationFrame(tick);
  }, []);

  // Recalc FullCalendar layout when view changes (fill-height parent)
  useEffect(() => {
    const api = calendarRef.current?.getApi();
    if (!api) return;
    const id = window.requestAnimationFrame(() => {
      api.updateSize();
      // After size settles, vertical-scroll month to today (same role as scrollTime for timegrid)
      window.setTimeout(() => scrollToTodayInMonthIfNeeded(), 0);
    });
    return () => window.cancelAnimationFrame(id);
  }, [currentView, isMobile, scrollToTodayInMonthIfNeeded]);
  const [currentTitle, setCurrentTitle] = useState<string>('');
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const [showLeadFollowups, setShowLeadFollowups] = useState<boolean>(true);
  const [showPersonalEvents, setShowPersonalEvents] = useState<boolean>(true);
  const [showRightsEnds, setShowRightsEnds] = useState<boolean>(true);
  const [moreEventsModal, setMoreEventsModal] = useState<{ date: Date; events: FullCalendarEvent[] } | null>(null);

  // State to track current view for optimization
  const [viewDates, setViewDates] = useState<{start: Date, end: Date} | null>(null);

  // Function to get follow-up color based on date
  const getFollowUpColor = (nextActionAt: Date): string => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const followUpDate = new Date(nextActionAt);
    followUpDate.setHours(0, 0, 0, 0);

    if (followUpDate < today) {
      return calendarEventColors.red; // overdue
    } else if (followUpDate.getTime() === today.getTime()) {
      return calendarEventColors.blue; // today
    } else {
      return calendarEventColors.emerald; // future
    }
  };

  // Centralized function to get event styles and text color (used in calendar and modal) - matches FullCalendar style exactly
  // This ensures the modal events have the same appearance as calendar events
  const getEventStyles = useCallback((color: string | undefined) => {
    const pastelCalendarColors = Object.values(calendarEventColors) as string[];
    const eventColor = color || calendarEventColors.default;
    
    // Get the dark text color (same logic as in transformToFullCalendarEvents)
    const getReadableTextColor = (background: string): string => {
      if (pastelCalendarColors.includes(background)) {
        return getCalendarEventTextColor(background);
      }
      if (background.startsWith('rgb')) return '#ffffff';
      const hex = background.replace('#', '').trim();
      if (hex.length !== 3 && hex.length !== 6) return '#ffffff';
      const full = hex.length === 3 ? hex.split('').map((c) => c + c).join('') : hex;
      const r = parseInt(full.slice(0, 2), 16);
      const g = parseInt(full.slice(2, 4), 16);
      const b = parseInt(full.slice(4, 6), 16);
      const luminance = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
      return luminance > 0.7 ? '#0f172a' : '#ffffff';
    };
    
    const textColor = getReadableTextColor(eventColor);
    
    return {
      backgroundColor: eventColor,
      borderColor: eventColor,
      // Match FullCalendar's .fc-event-main style: box-shadow: inset 4px 0 0 currentColor
      // currentColor is the text color (dark version), not the background color
      boxShadow: `inset 4px 0 0 ${textColor}`,
      color: textColor, // Apply text color to match calendar
    };
  }, []);

  // Transform DBCalendarEvent to FullCalendar format
  const transformToFullCalendarEvents = useCallback((events: DBCalendarEvent[]): FullCalendarEvent[] => {
    const pastelCalendarColors = Object.values(calendarEventColors) as string[];

    const getReadableTextColor = (background: string): string => {
      // If it's one of our pastel calendar colors, use the curated mapping.
      if (pastelCalendarColors.includes(background)) {
        return getCalendarEventTextColor(background);
      }

      // rgba()/rgb() may appear in other contexts; default to white text.
      if (background.startsWith('rgb')) return '#ffffff';

      // Fallback: compute contrast for hex colors.
      const hex = background.replace('#', '').trim();
      if (hex.length !== 3 && hex.length !== 6) return '#ffffff';
      const full = hex.length === 3 ? hex.split('').map((c) => c + c).join('') : hex;
      const r = parseInt(full.slice(0, 2), 16);
      const g = parseInt(full.slice(2, 4), 16);
      const b = parseInt(full.slice(4, 6), 16);
      // Relative luminance approximation (sRGB)
      const luminance = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
      return luminance > 0.7 ? '#0f172a' : '#ffffff';
    };

    return events.map(event => ({
      id: event.id,
      title: event.title,
      start: event.startDate,
      end: event.endDate ?? undefined,
      color: event.color || calendarEventColors.default,
      backgroundColor: event.color || calendarEventColors.default,
      borderColor: event.color || calendarEventColors.default,
      textColor: getReadableTextColor(event.color || calendarEventColors.default),
      extendedProps: {
        type: event.type,
        leadId: event.leadId,
        description: event.description
      }
    }));
  }, []);

  // Compute all events using useMemo for performance - used directly by FullCalendar
  const allEvents = useMemo(() => {
    // Database events
    const dbEvents = showPersonalEvents ? transformToFullCalendarEvents(events) : [];
    
    // Follow-up events from leads
    const followUpEvents: FullCalendarEvent[] = showLeadFollowups ? leads
      .filter(lead => {
        if (!lead.nextActionAt) return false;
        
        // Filter by view dates if available for performance
        if (viewDates) {
          const followUpDate = new Date(lead.nextActionAt);
          followUpDate.setHours(0, 0, 0, 0);
          return followUpDate >= viewDates.start && followUpDate < viewDates.end;
        }
        
        return true;
      })
      .map(lead => {
        const displayDate = new Date(lead.nextActionAt!);
        displayDate.setHours(0, 0, 0, 0);
        
        return {
          id: `follow-up-${lead.id}`,
          title: `🎯 ${lead.name}`,
          start: displayDate,
          end: undefined,
          allDay: true,
          color: getFollowUpColor(lead.nextActionAt!),
          backgroundColor: getFollowUpColor(lead.nextActionAt!),
          borderColor: getFollowUpColor(lead.nextActionAt!),
          textColor: getCalendarEventTextColor(getFollowUpColor(lead.nextActionAt!)),
          extendedProps: {
            type: 'follow_up',
            leadId: lead.id,
            description: `${t('calendar.followUpScheduled')} ${lead.name} (${lead.contact_value || t('leads.noContact')})`,
            isLeadEvent: true
          }
        };
      }) : [];

    const rightsColor = calendarEventColors.purple;
    const rightsEvents: FullCalendarEvent[] = showRightsEnds
      ? rightsProjects
          .filter((project) => {
            if (!project.rights?.endDate) return false;
            if (viewDates) {
              const endDate = new Date(project.rights.endDate);
              endDate.setHours(0, 0, 0, 0);
              return endDate >= viewDates.start && endDate < viewDates.end;
            }
            return true;
          })
          .map((project) => {
            const displayDate = new Date(project.rights!.endDate!);
            displayDate.setHours(0, 0, 0, 0);
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const isOverdue = displayDate.getTime() < today.getTime();
            const emoji = isOverdue ? '⚠️' : '⏰';
            const eventColor = isOverdue ? calendarEventColors.red : rightsColor;
            return {
              id: `rights-end-${project.id}`,
              title: `${emoji} ${t('calendar.rightsEndTitle', { name: project.name })}`,
              start: displayDate,
              end: undefined,
              allDay: true,
              editable: false,
              startEditable: false,
              durationEditable: false,
              color: eventColor,
              backgroundColor: eventColor,
              borderColor: eventColor,
              textColor: getCalendarEventTextColor(eventColor),
              extendedProps: {
                type: 'rights_expiration' as const,
                projectId: project.id,
                description: t('calendar.rightsEndDescription', { name: project.name }),
                isRightsEvent: true,
              },
            };
          })
      : [];

    return [...dbEvents, ...followUpEvents, ...rightsEvents];
  }, [events, leads, rightsProjects, showPersonalEvents, showLeadFollowups, showRightsEnds, viewDates, transformToFullCalendarEvents, t]);

  const handleDateClick = (info: any) => {
    setSelectedDate(info.date);
    setEditingEvent(null);
    openModal();
  };

  const handleEventClick = (info: any) => {
    const clickedEvent = info.event;
    const eventId = clickedEvent.id;
    
    // Prevent navigation if it's a link
    if (info.jsEvent) {
      info.jsEvent.preventDefault();
    }
    
    // Check if it's a follow-up event (starts with "follow-up-")
    if (eventId && eventId.startsWith('follow-up-')) {
      const leadId = clickedEvent.extendedProps?.leadId;
      if (leadId) {
        const lead = leads.find(l => l.id === leadId);
        if (lead) {
          setSelectedLead(lead);
          setIsFollowUpModalOpen(true);
        }
      }
      return;
    }

    // Rights end date → open project
    if (eventId && eventId.startsWith('rights-end-')) {
      const projectId = clickedEvent.extendedProps?.projectId || eventId.replace('rights-end-', '');
      if (projectId) {
        navigateToProject(navigate, projectId);
      }
      return;
    }
    
    // Regular calendar event - create FullCalendarEvent from clicked event
    const fullCalendarEvent: FullCalendarEvent = {
      id: clickedEvent.id,
      title: clickedEvent.title,
      start: clickedEvent.start,
      end: clickedEvent.end,
      color: clickedEvent.backgroundColor || clickedEvent.borderColor || calendarEventColors.default,
      extendedProps: {
        type: clickedEvent.extendedProps?.type || 'other',
        leadId: clickedEvent.extendedProps?.leadId,
        description: clickedEvent.extendedProps?.description
      }
    };
    
    setEditingEvent(fullCalendarEvent);
    setSelectedDate(null); // Clear selected date when editing existing event
    setInternalIsModalOpen(true); // Directly set to true to ensure modal opens
  };


  const handleEventDrop = async (info: any) => {
    const eventId = info.event.id;
    const eventExtendedProps = info.event.extendedProps || {};

    // Follow-up (lead) events: protect against out-of-order failures reverting newer moves
    if (eventId && eventId.startsWith('follow-up-')) {
      const leadId = eventExtendedProps.leadId;
      if (!leadId) return;

      const seq = (followUpDropSeqRef.current[leadId] ?? 0) + 1;
      followUpDropSeqRef.current[leadId] = seq;

      const newDate = new Date(info.event.start);
      newDate.setHours(0, 0, 0, 0);

      try {
        if (onUpdateEvent) {
          await onUpdateEvent({
            id: leadId,
            type: 'lead_update' as any,
            nextActionAt: newDate,
            leadId: leadId
          } as any);
        }
      } catch (error: any) {
        console.error('Error updating follow-up:', error);
        // Revert only if this is still the latest drop for this lead.
        if ((followUpDropSeqRef.current[leadId] ?? 0) === seq) {
          info.revert();
        }
      }
      return;
    }

    // Rights end dates are read-only on the calendar
    if (eventId && eventId.startsWith('rights-end-')) {
      info.revert();
      return;
    }

    // Regular calendar events
    try {
      const updatedEvent: DBCalendarEvent = {
        id: eventId,
        title: info.event.title,
        startDate: info.event.start,
        endDate: info.event.end ?? null,
        color: info.event.backgroundColor || info.event.borderColor || calendarEventColors.default,
        type: eventExtendedProps.type || 'other',
        leadId: eventExtendedProps.leadId,
        description: eventExtendedProps.description
      };

      await onUpdateEvent(updatedEvent);
    } catch (error: any) {
      console.error('Error updating event:', error);
      info.revert();
    }
  };

  const handleDatesSet = (info: any) => {
    setViewDates({
      start: info.view.activeStart,
      end: info.view.activeEnd
    });
    if (onViewDatesChange) {
      onViewDatesChange(info.view.activeStart, info.view.activeEnd);
    }
  };

  const handleSaveEvent = (eventData: any) => {
    const newEvent: Omit<DBCalendarEvent, 'id'> = {
      title: eventData.title,
      startDate: eventData.start,
      // IMPORTANT: null means "no end date" (so updates can clear existing end_date)
      endDate: eventData.end ?? null,
      color: eventData.color || calendarEventColors.default,
      type: eventData.type,
      // Important: don't send empty string to Supabase for UUID column
      leadId: eventData.leadId ? eventData.leadId : undefined,
      description: eventData.description
    };

    // Close modal immediately for snappy UX
    handleModalClose();
    
    // Save in background
    if (editingEvent) {
      const previousEvent = events.find(e => e.id === editingEvent.id) ?? undefined;
      Promise.resolve(onUpdateEvent({ ...newEvent, id: editingEvent.id }, { previousItem: previousEvent })).catch(error => {
        console.error('Error updating event:', error);
      });
    } else {
      Promise.resolve(onAddEvent(newEvent)).catch(error => {
        console.error('Error adding event:', error);
      });
    }
  };

  const handleDeleteEvent = () => {
    if (editingEvent) {
      onDeleteEvent(editingEvent.id);
      handleModalClose();
    }
  };

  const handleFollowUpSave = (lead: Lead, newDate: Date) => {
    // Close modal immediately for snappy UX
    setIsFollowUpModalOpen(false);
    setSelectedLead(null);
    
    // Update lead's nextActionAt via calendar event update in background
    const leadUpdateEvent = {
      id: lead.id,
      type: 'lead_update' as any,
      nextActionAt: newDate,
      leadId: lead.id
    };
    Promise.resolve(onUpdateEvent(leadUpdateEvent as any)).catch(error => {
      console.error('Error updating follow-up:', error);
    });
  };

  const eventTypeOptions = [
    { value: 'follow_up', label: t('calendar.followUp') },
    { value: 'script', label: t('calendar.script') },
    { value: 'shooting', label: t('calendar.shooting') },
    { value: 'deadline', label: t('calendar.deadline') },
    { value: 'meeting', label: t('calendar.meeting') },
    { value: 'other', label: t('calendar.other') },
  ];

  const colorOptions = [
    // Calendar-perfect palette: distinct, readable, not too long
    { value: calendarEventColors.blue, label: t('calendar.blue') },
    { value: calendarEventColors.cyan, label: t('calendar.cyan') },
    { value: calendarEventColors.emerald, label: t('calendar.green') },
    { value: calendarEventColors.amber, label: t('calendar.yellow') },
    { value: calendarEventColors.orange, label: t('calendar.orange') },
    { value: calendarEventColors.rose, label: t('calendar.rose') },
    { value: calendarEventColors.red, label: t('calendar.red') },
    { value: calendarEventColors.purple, label: t('calendar.purple') },
  ];

  // Handle external modal control
  useEffect(() => {
    if (externalIsModalOpen !== undefined && externalIsModalOpen) {
      setSelectedDate(new Date());
      setEditingEvent(null);
      openModal();
    }
  }, [externalIsModalOpen]);

  // Intercept clicks on "+X more" links to prevent default popover
  useEffect(() => {
    const handleMoreLinkClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const moreLink = target.closest('.fc-more-link');
      if (moreLink) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        
        // Get the date from the link's data attribute or parent cell
        const dayCell = moreLink.closest('.fc-daygrid-day');
        if (dayCell) {
          const dateStr = dayCell.getAttribute('data-date');
          if (dateStr) {
            const date = new Date(dateStr);
            // Get all events for this day
            const dayEvents = allEvents.filter(event => {
              const eventDate = new Date(event.start);
              return eventDate.toDateString() === date.toDateString();
            });
            setMoreEventsModal({ date, events: dayEvents });
          }
        }
      }
    };

    // Use capture phase to intercept before FullCalendar
    document.addEventListener('click', handleMoreLinkClick, true);

    return () => {
      document.removeEventListener('click', handleMoreLinkClick, true);
    };
  }, [allEvents]);

  return (
    <div className="flex flex-col flex-1 min-h-0">
      <Card padding="none" className="flex-1 min-h-0 overflow-hidden">
        {/* Mobile: half of layout gutter (px-3) so total margin isn’t doubled; desktop: standard padding */}
        <div className="flex flex-col flex-1 min-h-0 p-1.5 sm:p-6">
          {/* Calendar Controls — nav + filters left, period + view selector right */}
          <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2 mb-4 shrink-0">
            <div className="flex items-center gap-2 flex-wrap min-w-0">
              <div
                className="inline-flex items-center rounded-full bg-gray-100/90 p-1"
                role="group"
                aria-label={t('common.today')}
              >
                <button
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => calendarRef.current?.getApi().prev()}
                  aria-label={t('calendar.previousPeriod')}
                  className="inline-flex h-7 w-7 items-center justify-center rounded-full text-gray-500 transition-colors hover:bg-white/80 hover:text-gray-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/40"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => calendarRef.current?.getApi().today()}
                  className="inline-flex h-7 items-center rounded-full bg-white px-3 text-xs font-medium text-primary-700 shadow-sm ring-1 ring-primary-200/70 transition-colors hover:bg-primary-50/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/40 sm:text-sm"
                >
                  {t('common.today')}
                </button>
                <button
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => calendarRef.current?.getApi().next()}
                  aria-label={t('calendar.nextPeriod')}
                  className="inline-flex h-7 w-7 items-center justify-center rounded-full text-gray-500 transition-colors hover:bg-white/80 hover:text-gray-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/40"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>

              <div
                className="inline-flex max-w-full flex-wrap items-center gap-0.5 rounded-full bg-gray-100/90 p-1"
                role="group"
                aria-label={t('calendar.title')}
              >
                {(
                  [
                    {
                      key: 'followups',
                      pressed: showLeadFollowups,
                      onToggle: () => setShowLeadFollowups((prev) => !prev),
                      label: t('calendar.showLeadFollowups'),
                      dot: 'bg-blue-500',
                    },
                    {
                      key: 'rights',
                      pressed: showRightsEnds,
                      onToggle: () => setShowRightsEnds((prev) => !prev),
                      label: t('calendar.showRightsEnds'),
                      dot: 'bg-purple-500',
                    },
                    {
                      key: 'personal',
                      pressed: showPersonalEvents,
                      onToggle: () => setShowPersonalEvents((prev) => !prev),
                      label: t('calendar.showPersonalEvents'),
                      dot: 'bg-emerald-500',
                    },
                  ] as const
                ).map((filter) => (
                  <button
                    key={filter.key}
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={filter.onToggle}
                    aria-pressed={filter.pressed}
                    className={`inline-flex h-7 items-center gap-1.5 rounded-full px-2.5 text-xs font-medium whitespace-nowrap transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/40 sm:px-3 ${
                      filter.pressed
                        ? 'bg-white text-primary-700 shadow-sm ring-1 ring-primary-200/70'
                        : 'text-gray-500 hover:bg-white/70 hover:text-gray-800'
                    }`}
                  >
                    <span
                      className={`h-1.5 w-1.5 shrink-0 rounded-full ${filter.dot} ${
                        filter.pressed ? 'opacity-100' : 'opacity-40'
                      }`}
                      aria-hidden
                    />
                    {filter.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 min-w-0 ml-auto sm:gap-3">
              {currentTitle ? (
                <span className="hidden h-7 max-w-[12rem] items-center truncate rounded-full bg-gray-100/90 px-2.5 text-xs font-medium text-gray-600 sm:inline-flex md:max-w-none">
                  {currentTitle}
                </span>
              ) : null}
              <SegmentedToggle
                value={currentView}
                onChange={(val) => {
                  setCurrentView(val);
                  const api = calendarRef.current?.getApi();
                  if (!api) return;
                  if (val === 'month') api.changeView('dayGridMonth');
                  if (val === 'week') api.changeView('timeGridWeek');
                  if (val === 'day') api.changeView('timeGridDay');
                }}
                options={[
                  { key: 'month', label: t('calendar.month') },
                  { key: 'week', label: t('calendar.week') },
                  { key: 'day', label: t('calendar.day') },
                ]}
                size="sm"
                ariaLabel="Calendar view"
              />
            </div>
          </div>

          {/* Absolute fill so height="100%" + scrollTime resolve against a definite box */}
          <div className="relative flex-1 min-h-0 min-w-0">
          {/* FullCalendar overrides: inline styles + !important needed to win over library defaults. */}
          <style dangerouslySetInnerHTML={{
            __html: `
              /* ============================================
                 VARIABLES + BASE
                 ============================================ */
              .fc {
                --fc-week-min-width: 840px;
                --fc-week-shrink-min: 4.5rem;
                height: 100% !important;
                font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif !important;
                border-radius: 0.75rem !important;
                overflow: hidden !important;
                /* Outer frame so corners keep a visible border */
                border: 1px solid #e5e7eb !important;
                background: #ffffff !important;
              }
              
              /* Table headers - Modern, clean */
              .fc-theme-standard td,
              .fc-theme-standard th {
                border-color: #e5e7eb !important;
              }
              
              /* Day headers */
              .fc-col-header-cell {
                padding: 0.75rem 0.5rem !important;
                background: #f9fafb !important;
                font-weight: 600 !important;
                font-size: 0.75rem !important;
                text-transform: uppercase !important;
                letter-spacing: 0.05em !important;
                color: #6b7280 !important;
                border-color: #e5e7eb !important;
              }
              
              /* Day cells */
              .fc-daygrid-day {
                border-color: #e5e7eb !important;
                transition: background-color 0.15s ease !important;
              }
              
              .fc-daygrid-day:hover {
                background-color: #f9fafb !important;
              }
              
              /* Highlight "today" — fixed blue (aligns with follow-up traffic light) */
              .fc-daygrid .fc-day-today {
                background-color: #eff6ff !important;
              }
              .fc-timegrid-col.fc-day-today {
                background-color: #eff6ff !important;
              }

              .fc-daygrid .fc-day-today .fc-daygrid-day-number {
                color: #2563eb !important;
                font-weight: 600 !important;
              }

              /* Day view: remove the "today" blue background */
              .fc-timeGridDay-view .fc-timegrid-col.fc-day-today {
                background-color: transparent !important;
              }
              
              /* Day numbers */
              .fc-daygrid-day-number {
                padding: 0.5rem !important;
                font-size: 0.875rem !important;
                color: #374151 !important;
                font-weight: 500 !important;
              }
              
              .fc-day-other .fc-daygrid-day-number {
                color: #d1d5db !important;
              }
              
              /* ============================================
                 MODERN EVENT STYLES
                 ============================================ */
              .fc-event {
                cursor: move !important;
                border: none !important;
                border-radius: 0.375rem !important;
                /* Let .fc-event-main draw the left color bar */
                padding: 0 !important;
                margin: 0.125rem 0.25rem !important;
                font-size: 0.75rem !important;
                font-weight: 500 !important;
                line-height: 1.4 !important;
                /* Keep rounded look even with inner left bar */
                overflow: hidden !important;
              }
              
              .fc-event:hover {
                cursor: grab !important;
              }
              
              .fc-event:active {
                cursor: grabbing !important;
              }
              
              .fc-event-title-row {
                display: flex !important;
                align-items: flex-start !important;
                gap: 0.25rem !important;
                min-width: 0 !important;
              }
              .fc-event-personal-icon {
                flex-shrink: 0 !important;
                display: inline-flex !important;
                opacity: 0.9 !important;
              }
              .fc-event-personal-icon-svg {
                width: 0.875rem !important;
                height: 0.875rem !important;
              }
              .fc-event-title {
                font-weight: 500 !important;
                padding: 0 !important;
                min-width: 0 !important;
                overflow: hidden !important;
                text-overflow: ellipsis !important;
                white-space: nowrap !important;
              }

              /* Description: only used in day view (controlled) */
              .fc-event-description {
                display: -webkit-box !important;
                -webkit-box-orient: vertical !important;
                overflow: hidden !important;
                white-space: normal !important;
                text-overflow: ellipsis !important;
                opacity: 0.85 !important;
                font-size: 0.6875rem !important; /* ~11px */
                line-height: 1.2 !important;
                margin-top: 0.125rem !important;
              }

              /* TimeGrid (week/day): keep titles readable for short events */
              .fc-timegrid-event .fc-event-title {
                white-space: nowrap !important;
                overflow: hidden !important;
                text-overflow: ellipsis !important;
                line-height: 1.2 !important;
              }

              /* TimeGrid: ensure content fills the event height */
              .fc-timegrid-event .fc-event-main {
                height: 100% !important;
              }

              /* Week/Day views have more space: slightly larger typography (month stays unchanged) */
              .fc-timeGridWeek-view .fc-event,
              .fc-timeGridDay-view .fc-event {
                font-size: 0.8125rem !important; /* 13px */
              }

              .fc-timeGridWeek-view .fc-event-time,
              .fc-timeGridDay-view .fc-event-time {
                font-size: 0.75rem !important; /* 12px */
                opacity: 0.95 !important;
              }

              .fc-timeGridWeek-view .fc-event-description,
              .fc-timeGridDay-view .fc-event-description {
                font-size: 0.75rem !important; /* 12px */
              }
              
              /* Event colors - modern gradients; extra right padding so text isn’t clipped */
              .fc-event-main {
                display: block !important;
                min-width: 0 !important;
                padding: 0.25rem 0.75rem 0.25rem 0.5rem !important;
                /* Google Calendar-like left bar using the actual text color */
                box-shadow: inset 4px 0 0 currentColor !important;
                border-radius: inherit !important;
              }
              
              /* ============================================
                 DRAG & DROP - Standard
                 ============================================ */
              .fc-event-dragging,
              .fc-event-mirror {
                opacity: 0.8 !important;
                cursor: grabbing !important;
                z-index: 9999 !important;
                pointer-events: none !important;
                box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
                transform: none !important;
              }
              
              .fc-event-mirror {
                margin: 0 !important;
                transition: background-color 0.1s ease, border-color 0.1s ease !important;
              }
              
              /* Highlight color based on date - takes full row height but only in highlighted cell */
              .fc-daygrid-day {
                position: relative !important;
                vertical-align: top !important;
              }
              
              .fc-daygrid-day-frame {
                position: relative !important;
                min-height: 100px !important;
                height: 100% !important;
              }
              
              .fc-highlight {
                border: 2px solid transparent !important;
                position: absolute !important;
                top: 0 !important;
                left: 0 !important;
                right: 0 !important;
                width: 100% !important;
                box-sizing: border-box !important;
                z-index: 0 !important;
                pointer-events: none !important;
                margin: 0 !important;
                bottom: 0 !important;
              }
              
              .fc-day-past .fc-highlight {
                background: rgba(239, 68, 68, 0.15) !important;
                border-color: rgba(239, 68, 68, 0.4) !important;
              }
              
              .fc-day-today .fc-highlight {
                background: rgba(59, 130, 246, 0.15) !important;
                border-color: rgba(59, 130, 246, 0.4) !important;
              }
              
              .fc-day-future .fc-highlight {
                background: rgba(16, 185, 129, 0.15) !important;
                border-color: rgba(16, 185, 129, 0.4) !important;
              }
              
              
              /* ============================================
                 "+X MORE" LINKS - Modern design
                 ============================================ */
              .fc-more-link {
                display: block !important;
                width: 100% !important;
                text-align: center !important;
                color: #fff !important;
                border-radius: 0.375rem !important;
                padding: 0.25rem 0.5rem !important;
                font-size: 0.75rem !important;
                font-weight: 500 !important;
                text-decoration: none !important;
                transition: all 0.2s ease !important;
                box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1) !important;
              }
              
              .fc-day-past .fc-more-link {
                background: linear-gradient(to right, rgba(239, 68, 68, 0.8), rgba(220, 38, 38, 0.8)) !important;
              }
              
              .fc-day-today .fc-more-link {
                background: linear-gradient(to right, rgba(59, 130, 246, 0.8), rgba(37, 99, 235, 0.8)) !important;
              }
              
              .fc-day-future .fc-more-link {
                background: linear-gradient(to right, rgba(16, 185, 129, 0.8), rgba(5, 150, 105, 0.8)) !important;
              }
              
              .fc-more-link:hover {
                transform: translateY(-1px) !important;
                box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15) !important;
              }
              
              .fc-day-past .fc-more-link:hover { 
                background: linear-gradient(to right, rgba(239, 68, 68, 0.95), rgba(220, 38, 38, 0.95)) !important;
              }
              
              .fc-day-today .fc-more-link:hover { 
                background: linear-gradient(to right, rgba(59, 130, 246, 0.95), rgba(37, 99, 235, 0.95)) !important;
              }
              
              .fc-day-future .fc-more-link:hover { 
                background: linear-gradient(to right, rgba(16, 185, 129, 0.95), rgba(5, 150, 105, 0.95)) !important;
              }
              
              /* Hide default FullCalendar popover - we use our custom modal instead */
              .fc-popover {
                display: none !important;
                visibility: hidden !important;
                pointer-events: none !important;
              }
              
              /* ============================================
                 NAVIGATION BUTTONS - Modern design
                 ============================================ */
              .fc-button {
                background: #fff !important;
                border: 1px solid #e5e7eb !important;
                color: #374151 !important;
                padding: 0.5rem 1rem !important;
                border-radius: 0.5rem !important;
                font-weight: 500 !important;
                font-size: 0.875rem !important;
                transition: all 0.2s ease !important;
                box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05) !important;
              }
              
              .fc-button:hover {
                background: #f9fafb !important;
                border-color: #d1d5db !important;
                box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1) !important;
                transform: translateY(-1px) !important;
              }
              
              .fc-button:active {
                transform: translateY(0) !important;
                box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05) !important;
              }
              
              .fc-button-primary:not(:disabled):active,
              .fc-button-primary:not(:disabled).fc-button-active {
                background: rgb(var(--primary-500)) !important;
                border-color: rgb(var(--primary-600)) !important;
                color: #fff !important;
                box-shadow: 0 2px 4px rgb(var(--primary-500) / 0.3) !important;
              }
              
              .fc-button:disabled {
                opacity: 0.4 !important;
                cursor: not-allowed !important;
              }
              
              /* ============================================
                 TIME GRID (Week/Day view) - Modern
                 ============================================ */
              /* All-day area: remove default yellow-ish tint */
              .fc-timegrid .fc-timegrid-axis,
              .fc-timegrid .fc-timegrid-axis-frame,
              .fc-timegrid .fc-timegrid-axis-cushion,
              .fc-timegrid .fc-timegrid-slot-label,
              .fc-timegrid .fc-timegrid-slot-label-frame,
              .fc-timegrid .fc-timegrid-slot-label-cushion,
              .fc-timegrid .fc-timegrid-slot-lane,
              .fc-timegrid .fc-timegrid-allday,
              .fc-timegrid .fc-timegrid-all-day,
              .fc-timegrid .fc-timegrid-row.fc-timegrid-row-allday {
                background-color: transparent !important;
              }

              .fc-timegrid-slot {
                border-color: #f3f4f6 !important;
                height: 2.5rem !important;
              }
              
              .fc-timegrid-slot-label {
                border-color: #e5e7eb !important;
                color: #6b7280 !important;
                font-size: 0.75rem !important;
                padding: 0.5rem !important;
              }
              
              .fc-timegrid-col {
                border-color: #e5e7eb !important;
              }
              
              .fc-timegrid-now-indicator-line {
                border-color: #ef4444 !important;
                border-width: 2px !important;
              }
              
              .fc-timegrid-now-indicator-arrow {
                border-color: #ef4444 !important;
              }
              
              /* ============================================
                 WEEK VIEW (timeGridWeek) - Scroll + alignment
                 ============================================ */
              /* One horizontal scroll for whole view (header + all-day + body). */
              .fc-timeGridWeek-view {
                overflow-x: auto !important;
                overflow-y: hidden !important;
                width: 100% !important;
                min-width: 0 !important;
                height: 100% !important;
              }
              .fc-timeGridWeek-view .fc-scrollgrid,
              .fc-timeGridWeek-view .fc-scroller-liquid,
              .fc-timeGridWeek-view .fc-scroller > div,
              .fc-timeGridWeek-view .fc-timegrid-body,
              .fc-timeGridWeek-view .fc-timegrid-body table {
                min-width: var(--fc-week-min-width) !important;
              }
              .fc-timeGridWeek-view .fc-scroller {
                overflow-x: hidden !important;
                overflow-y: auto !important;
              }
              .fc-timeGridWeek-view .fc-scroller > div {
                width: 100% !important;
                min-width: 100% !important;
                max-width: 100% !important;
              }
              .fc-timeGridWeek-view .fc-scrollgrid-shrink,
              .fc-timeGridWeek-view .fc-timegrid-axis,
              .fc-timeGridWeek-view .fc-col-header .fc-scrollgrid-shrink {
                min-width: var(--fc-week-shrink-min) !important;
              }

              /* ============================================
                 SCROLLBARS HIDDEN + reclaim FC gutter
                 FullCalendar shrinks tables by scrollbarWidth and may set
                 overflow-y: scroll; we hide the bar and fill that space.
                 ============================================ */
              .fc .fc-scroller {
                scrollbar-width: none !important;
                -ms-overflow-style: none !important;
                margin-right: 0 !important;
                right: 0 !important;
              }
              .fc .fc-scroller::-webkit-scrollbar {
                display: none !important;
                width: 0 !important;
                height: 0 !important;
              }
              /* Prefer auto over FC's scroll so classic browsers don't reserve a strip */
              .fc-timeGridWeek-view .fc-scroller,
              .fc-timeGridDay-view .fc-scroller,
              .fc-dayGridMonth-view .fc-scroller {
                overflow-y: auto !important;
              }
              .fc .fc-scrollgrid table,
              .fc .fc-scrollgrid-sticky-shim {
                width: 100% !important;
              }
              
              /* ============================================
                 GENERAL IMPROVEMENTS
                 ============================================ */
              .fc-toolbar-title {
                font-size: 1.25rem !important;
                font-weight: 600 !important;
                color: #111827 !important;
              }
              
              .fc-daygrid-day-frame {
                min-height: 100px !important;
                position: relative !important;
              }
              
              .fc-daygrid-day {
                position: relative !important;
              }
              
              /* Mobile: breakpoint must match MOBILE_BREAKPOINT (useMediaQuery) */
              @media (max-width: ${MOBILE_BREAKPOINT_PX}px) {
                .fc-col-header-cell {
                  padding: 0.25rem 0.125rem !important;
                  font-size: 0.625rem !important;
                  min-width: 0 !important;
                  overflow: hidden !important;
                  text-overflow: ellipsis !important;
                  white-space: nowrap !important;
                }
                .fc .fc-scroller {
                  overflow-x: auto !important;
                  overflow-y: auto !important;
                  scrollbar-width: none !important;
                  -ms-overflow-style: none !important;
                }
                /* Vue semaine : le scroll horizontal est sur le conteneur .fc-timeGridWeek-view, pas sur les scrollers */
                .fc-timeGridWeek-view .fc-scroller {
                  overflow-x: hidden !important;
                }
                .fc-timeGridWeek-view {
                  scrollbar-width: none !important;
                  -ms-overflow-style: none !important;
                }
                .fc-timeGridWeek-view::-webkit-scrollbar {
                  display: none !important;
                  width: 0 !important;
                  height: 0 !important;
                }
                .fc .fc-scroller::-webkit-scrollbar {
                  display: none !important;
                  width: 0 !important;
                  height: 0 !important;
                }
                .fc-event {
                  font-size: 0.75rem !important;
                }
                .fc-event-main {
                  padding: 0.25rem 0.75rem 0.25rem 0.5rem !important;
                }
                .fc-daygrid-day-number {
                  padding: 0.375rem !important;
                  font-size: 0.75rem !important;
                }
              }
            `
          }} />
          <div className="absolute inset-0" ref={calendarHostRef}>
          <FullCalendar
            ref={calendarRef}
            plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
            initialView="dayGridMonth"
            headerToolbar={false}
            allDayText={''}
            events={allEvents}
            // Make very short events (e.g. 15min) still readable (2 lines: time + title)
            eventMinHeight={38}
            eventContent={(arg: EventContentArg) => {
              const viewType = arg.view.type; // 'dayGridMonth' | 'timeGridWeek' | 'timeGridDay' | ...
              const isWeek = viewType === 'timeGridWeek';
              const isDay = viewType === 'timeGridDay';
              const isTimeGrid = isWeek || isDay;
              const description = (arg.event.extendedProps as any)?.description as string | undefined;
              const isPersonalEvent = !(arg.event.extendedProps as any)?.isLeadEvent
                && !(arg.event.extendedProps as any)?.isRightsEvent;
              const showPersonalIcon = isMobile && isPersonalEvent;

              // Standard/practical:
              // - Week view: show 1-line description when there is enough space (keeps layout tidy)
              // - Day view: show more lines if the event is long enough
              // - All-day: 1 line max (row is compact)
              let descLines = 0;
              if (isTimeGrid && description) {
                const isAllDay = !!arg.event.allDay;
                const start = arg.event.start;
                const end = arg.event.end;
                const minutes =
                  isAllDay ? 24 * 60 : start && end ? Math.round((end.getTime() - start.getTime()) / 60000) : 0;

                if (isAllDay) {
                  descLines = 1;
                } else if (isWeek) {
                  // Keep week view compact: only if event is not too short
                  if (minutes >= 45) descLines = 1;
                } else if (isDay) {
                  if (minutes >= 30) descLines = 1;
                  if (minutes >= 60) descLines = 2;
                  if (minutes >= 120) descLines = 3;
                }
              }

              return (
                <div>
                  {!isMobile && arg.timeText ? <div className="fc-event-time">{arg.timeText}</div> : null}
                  <div className="fc-event-title-row">
                    {showPersonalIcon && (
                      <span className="fc-event-personal-icon" aria-hidden>
                        <CalendarDays className="fc-event-personal-icon-svg" aria-hidden />
                      </span>
                    )}
                    <span className="fc-event-title">{arg.event.title}</span>
                  </div>
                  {descLines > 0 && (
                    <div className="fc-event-description" style={{ WebkitLineClamp: descLines } as any}>
                      {description}
                    </div>
                  )}
                </div>
              );
            }}
            dateClick={handleDateClick}
            eventClick={handleEventClick}
            eventDrop={handleEventDrop}
            datesSet={(arg) => {
              handleDatesSet(arg);
              setCurrentTitle(arg.view.title);
              // Sync currentView when user navigates via API or internal changes
              const type = arg.view.type;
              if (type === 'dayGridMonth' && currentView !== 'month') setCurrentView('month');
              if (type === 'timeGridWeek' && currentView !== 'week') setCurrentView('week');
              if (type === 'timeGridDay' && currentView !== 'day') setCurrentView('day');
              scrollToTodayInMonthIfNeeded();
            }}
            editable={true}
            selectable={false}
            selectMirror={false}
            dayMaxEvents={isMobile ? 1 : 3}
            eventStartEditable={true}
            eventDurationEditable={true}
            dragScroll={true}
            fixedMirrorParent={document.body}
            moreLinkText={(num) => (isMobile ? `+${num}` : `+${num} ${t('followups.moreCount')}`)}
            weekends={true}
            locale={language === 'fr' ? frLocale : enLocale}
            height="100%"
            // Day/week: open around morning hours; full day stays scrollable (Google Calendar-style)
            scrollTime="08:00:00"
            scrollTimeReset={false}
            eventDisplay="block"
            eventTextColor="#0f172a"
            eventBackgroundColor={calendarEventColors.default}
            eventBorderColor={calendarEventColors.default}
          />
          </div>
          </div>
        </div>
      </Card>

      {/* Add/Edit Event Modal */}
      <EventModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        onSave={handleSaveEvent}
        onDelete={handleDeleteEvent}
        event={editingEvent}
        selectedDate={selectedDate}
        calendarView={currentView}
        leads={leads}
        eventTypeOptions={eventTypeOptions}
        colorOptions={colorOptions}
        eventTypeColors={eventTypeColors}
        onSearchLeads={onSearchLeads}
        onCreateLead={onCreateLead}
      />

      <FollowUpModal
        isOpen={isFollowUpModalOpen}
        onClose={() => {
          setIsFollowUpModalOpen(false);
          setSelectedLead(null);
        }}
        onSave={handleFollowUpSave}
        lead={selectedLead}
      />

      {/* More Events Modal - Standard SaaS: Scrollable modal for days with many events */}
      {moreEventsModal && (
        <Modal
          isOpen={!!moreEventsModal}
          onClose={() => setMoreEventsModal(null)}
          title={t('calendar.events') + ' - ' + new Date(moreEventsModal.date).toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-GB', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          })}
          size="md"
        >
          <div className="max-h-[60vh] overflow-y-auto space-y-2 pr-2">
            {moreEventsModal.events.length === 0 ? (
              <p className="text-gray-500 text-center py-4">{t('calendar.noEventsToExport')?.replace(' à exporter', '') || 'Aucun événement'}</p>
            ) : (
              moreEventsModal.events
                .sort((a, b) => {
                  // Sort by start time
                  const timeA = a.start.getTime();
                  const timeB = b.start.getTime();
                  return timeA - timeB;
                })
                .map((event) => {
                  return (
                    <div
                      key={event.id}
                      className="p-3 rounded-lg border border-gray-200 hover:border-gray-300 transition-colors cursor-pointer relative"
                      style={getEventStyles(event.color)}
                      onClick={() => {
                        setMoreEventsModal(null);
                        // Create a compatible event object for handleEventClick
                        const calendarEvent = calendarRef.current?.getApi().getEventById(event.id);
                        if (calendarEvent) {
                          handleEventClick({ event: calendarEvent, jsEvent: { preventDefault: () => {} } });
                        } else {
                          // Fallback: create a mock event object
                          handleEventClick({ 
                            event: {
                              id: event.id,
                              title: event.title,
                              start: event.start,
                              end: event.end,
                              allDay: event.allDay,
                              backgroundColor: event.color,
                              extendedProps: event.extendedProps
                            } as any,
                            jsEvent: { preventDefault: () => {} }
                          });
                        }
                      }}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-sm font-medium truncate" style={{ color: getEventStyles(event.color).color }}>
                              {event.title}
                            </span>
                          </div>
                          {event.start && (
                            <div className="text-xs" style={{ color: getEventStyles(event.color).color, opacity: 0.8 }}>
                              {event.allDay
                                ? t('calendar.allDayShort') || 'Toute la journée'
                                : new Date(event.start).toLocaleTimeString(language === 'fr' ? 'fr-FR' : 'en-GB', {
                                    hour: '2-digit',
                                    minute: '2-digit',
                                  })}
                              {event.end && !event.allDay && (
                                <> - {new Date(event.end).toLocaleTimeString(language === 'fr' ? 'fr-FR' : 'en-GB', {
                                    hour: '2-digit',
                                    minute: '2-digit',
                                  })}</>
                              )}
                            </div>
                          )}
                          {event.extendedProps?.description && (
                            <p className="text-xs mt-1 line-clamp-2" style={{ color: getEventStyles(event.color).color, opacity: 0.7 }}>
                              {event.extendedProps.description}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })
            )}
          </div>
        </Modal>
      )}
    </div>
  );
};

interface EventModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (eventData: any) => void;
  onDelete: () => void;
  event?: FullCalendarEvent | null;
  selectedDate?: Date | null;
  calendarView?: CalendarViewType;
  leads: Lead[];
  eventTypeOptions: { value: string; label: string }[];
  colorOptions: { value: string; label: string }[];
  eventTypeColors: Record<CalendarEventTypeId, string>;
  onUpdateLead?: (leadId: string, nextActionAt: Date | null) => void;
  onSearchLeads?: (term: string) => Promise<Lead[]>;
  onCreateLead?: (args: RequestCreateLeadArgs) => void;
}

const EventModal: React.FC<EventModalProps> = ({
  isOpen,
  onClose,
  onSave,
  onDelete,
  event,
  selectedDate,
  calendarView,
  leads,
  eventTypeOptions,
  colorOptions,
  eventTypeColors,
  onUpdateLead,
  onSearchLeads,
  onCreateLead,
}) => {
  const { t } = useI18n();
  const [dateRangeError, setDateRangeError] = useState<string | null>(null);
  const [titleError, setTitleError] = useState<string | null>(null);
  const [gcalConnected, setGcalConnected] = useState<boolean | null>(null);
  const defaultType: CalendarEventTypeId = 'other';
  const [formData, setFormData] = useState<{
    title: string;
    startDate: Date | null;
    endDate: Date | null;
    type: string;
    leadId: string;
    description: string;
    color: string;
  }>({
    title: '',
    startDate: null as Date | null,
    endDate: null as Date | null,
    type: defaultType,
    leadId: '',
    description: '',
    color: eventTypeColors[defaultType],
  });

  useEffect(() => {
    setDateRangeError(null);
    setTitleError(null);
    if (!isOpen) {
      setGcalConnected(null);
      return;
    }
    let cancelled = false;
    googleCalendarService
      .getStatus()
      .then((status) => {
        if (!cancelled) setGcalConnected(Boolean(status.connected));
      })
      .catch(() => {
        if (!cancelled) setGcalConnected(false);
      });
    if (event) {
      const startDate = event.start;
      const endDate = event.end || null;
      const type = (event.extendedProps.type as CalendarEventTypeId) || defaultType;
      setFormData({
        title: event.title,
        startDate,
        endDate,
        type,
        leadId: event.extendedProps.leadId || '',
        description: event.extendedProps.description || '',
        color: event.color || eventTypeColors[type] || calendarEventColors.default,
      });
    } else if (selectedDate) {
      // Month view: day click has no time → use default slot. Week/day view: use clicked slot (including 00:00).
      const startDate = new Date(selectedDate);
      const isTimeGridView = calendarView === 'week' || calendarView === 'day';
      if (!isTimeGridView) {
        startDate.setHours(DEFAULT_NEW_EVENT_START_HOUR, 0, 0, 0);
      }
      const endDate = new Date(startDate);
      if (!isTimeGridView) {
        endDate.setHours(DEFAULT_NEW_EVENT_END_HOUR, 0, 0, 0);
      } else {
        endDate.setTime(endDate.getTime() + ONE_HOUR_MS);
      }

      setFormData({
        title: '',
        startDate,
        endDate,
        type: defaultType,
        leadId: '',
        description: '',
        color: eventTypeColors[defaultType],
      });
    }
    return () => {
      cancelled = true;
    };
  }, [isOpen, event, selectedDate, calendarView]); // eslint-disable-line react-hooks/exhaustive-deps -- don't reset form when prefs hydrate

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.title?.trim()) {
      setTitleError(t('calendar.eventTitleRequired'));
      return;
    }
    if (!formData.startDate) return;
    if (formData.endDate && formData.endDate.getTime() < formData.startDate.getTime()) {
      setDateRangeError(t('calendar.endBeforeStart'));
      return;
    }

    onSave({
      ...formData,
      start: formData.startDate,
      end: formData.endDate ?? undefined,
    });
  };
  const selectedLeadForSearch = formData.leadId ? (leads.find((l) => l.id === formData.leadId) || null) : null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={event ? t('calendar.editEvent') : t('calendar.newEvent')} size="lg">
      <form onSubmit={handleSubmit} noValidate className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label={t('calendar.eventTitle')}
            placeholder={t('calendar.eventTitlePlaceholder')}
            value={formData.title}
            onChange={(e) => {
              setFormData({ ...formData, title: e.target.value });
              if (titleError) setTitleError(null);
            }}
            error={titleError || undefined}
          />
          
          <Select
            label={t('calendar.eventType')}
            value={formData.type}
            onChange={(value) => {
              const type = value as CalendarEventTypeId;
              setFormData({
                ...formData,
                type,
                color: eventTypeColors[type] || formData.color,
              });
            }}
            options={eventTypeOptions}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <DatePicker
            label={t('calendar.startDateLabel')}
            value={formData.startDate}
            onChange={(date) => {
              setFormData({ ...formData, startDate: date });
              if (dateRangeError) setDateRangeError(null);
            }}
            showTimeSelect
            allowClear={false}
            error={dateRangeError || undefined}
            showErrorText={false}
          />
          
          <DatePicker
            label={t('calendar.endDateLabel')}
            value={formData.endDate}
            onChange={(date) => {
              setFormData({ ...formData, endDate: date });
              if (dateRangeError) setDateRangeError(null);
            }}
            showTimeSelect
            error={dateRangeError || undefined}
            showErrorText={false}
          />

          {dateRangeError && (
            <p className="md:col-span-2 -mt-2 text-sm text-red-600">
              {dateRangeError}
            </p>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('calendar.associatedLead')}
            </label>
            <LeadSearch
              leads={leads}
              selectedLead={selectedLeadForSearch}
              onSelectLead={(lead) => setFormData({ ...formData, leadId: lead?.id || '' })}
              placeholder={t('calendar.searchLead')}
              searchFn={onSearchLeads}
              onCreateLead={onCreateLead}
            />
          </div>
          
          <Select
            label={t('calendar.eventColor')}
            value={formData.color}
            onChange={(value) => setFormData({ ...formData, color: value })}
            options={colorOptions}
          />
        </div>

        <TextArea
          label={t('calendar.eventDescription')}
          placeholder={t('calendar.eventDescriptionPlaceholder')}
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          rows={3}
        />

        {/* Google Calendar line: always reserved once a date is set (avoids modal height jump) */}
        {formData.startDate ? (
          <div className="pt-1 min-h-[1.75rem] flex items-center">
            {gcalConnected === false ? (
              <Link
                to="/app/settings?tab=integrations&gcal=connect"
                onClick={onClose}
                className="inline-flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-1 rounded"
              >
                <Link2 className="w-4 h-4 shrink-0" aria-hidden />
                {t('calendar.setupGoogleCalendar')}
              </Link>
            ) : gcalConnected === true ? (
              <p className="text-sm text-gray-500 inline-flex items-center gap-2">
                <CalendarDays className="w-4 h-4 shrink-0" aria-hidden />
                {t('calendar.googleCalendarSynced')}
              </p>
            ) : null}
          </div>
        ) : null}

        <div className="flex justify-between">
          <div>
            {event && (
              <Button
                type="button"
                variant="danger"
                onClick={onDelete}
              >
                {t('common.delete')}
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button type="button" variant="ghost" size="md" onClick={onClose}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" variant="primary" size="md">
              {event ? t('common.edit') : t('common.create')}
            </Button>
          </div>
        </div>
      </form>
    </Modal>
  );
};

export default Calendar;

